if (!customElements.get("product-search-form")) {
  class ProductSearchForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("form");
      this.searchUrl =
        this.dataset.searchUrl || window.routes?.search_url || `${window.routes?.root || "/"}search`;
      this.searchSectionId = this.dataset.searchSectionId || "main-search";
      this.queryInput = this.querySelector("[data-product-search-query]");
      this.fields = Array.from(this.querySelectorAll("[data-product-search-field]"));
      this.submitButton = this.querySelector("[data-product-search-submit]");
      this.cacheKey = "home-product-search-vehicle-index-v2";

      if (!this.form || this.fields.length !== 3 || !this.submitButton || !this.queryInput) {
        return;
      }

      this.makerField = this.fields[0];
      this.carNameField = this.fields[1];
      this.carModelField = this.fields[2];

      this.placeholders = new Map(
        this.fields.map((field) => [field, field.querySelector("option")?.textContent || ""]),
      );

      this.handleChange = this.onFieldChange.bind(this);
      this.handleSubmit = this.onSubmit.bind(this);

      this.fields.forEach((field) => {
        field.addEventListener("change", this.handleChange);
      });

      this.form.addEventListener("submit", this.handleSubmit);
      this.updateSubmitState();
      this.initialize();
    }

    disconnectedCallback() {
      if (!this.fields || !this.handleChange) {
        return;
      }

      this.fields.forEach((field) => {
        field.removeEventListener("change", this.handleChange);
      });

      this.form?.removeEventListener("submit", this.handleSubmit);
    }

    normalizeText(value) {
      return value.replace(/\s+/g, " ").trim();
    }

    parseHtmlDocument(html) {
      return new DOMParser().parseFromString(html, "text/html");
    }

    escapeRegExp(value) {
      return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }

    resetField(field, { disabled = true } = {}) {
      const placeholder = this.placeholders.get(field) || "";
      field.innerHTML = "";
      field.append(new Option(placeholder, ""));
      field.value = "";
      field.disabled = disabled;
    }

    setFieldLoading(field) {
      const placeholder = this.placeholders.get(field) || "";
      field.innerHTML = "";
      field.append(new Option(`${placeholder}...`, ""));
      field.value = "";
      field.disabled = true;
    }

    populateFieldOptions(field, values, { disabled = false } = {}) {
      this.resetField(field, { disabled: true });

      if (!Array.isArray(values) || values.length === 0) {
        field.disabled = true;
        return;
      }

      values.forEach((value) => {
        field.append(new Option(value, value));
      });

      field.disabled = disabled;
    }

    getSelectedTerms() {
      return this.fields
        .map((field) => this.normalizeText(field.value))
        .filter((value) => value.length > 0);
    }

    buildSearchQuery(term) {
      if (window.stockItemSearch?.buildSearchQuery) {
        return window.stockItemSearch.buildSearchQuery(term);
      }

      const normalizedTerm = term.trim();
      if (!normalizedTerm.length) {
        return 'tag:"STOCK ITEM"';
      }

      return `${normalizedTerm} tag:"STOCK ITEM"`;
    }

    updateQuery() {
      this.queryInput.value = this.buildSearchQuery(this.getSelectedTerms().join(" "));
    }

    updateSubmitState() {
      this.updateQuery();
      this.submitButton.disabled = this.getSelectedTerms().length === 0;
    }

    buildSearchPageUrl(page = 1) {
      const url = new URL(this.searchUrl, window.location.origin);
      url.searchParams.set("type", "product");
      url.searchParams.set("q", this.buildSearchQuery(""));
      url.searchParams.set("section_id", this.searchSectionId);

      if (page > 1) {
        url.searchParams.set("page", String(page));
      }

      return url.toString();
    }

    async fetchPage(url) {
      const response = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });

      if (!response.ok) {
        throw new Error(`Failed to load vehicle search data: ${response.status}`);
      }

      return response.text();
    }

    getPageCount(document) {
      const pageNumbers = Array.from(document.querySelectorAll(".pagination .page-number"))
        .map((element) => Number(this.normalizeText(element.textContent || "")))
        .filter((value) => Number.isFinite(value));

      return Math.max(1, ...pageNumbers);
    }

    getFilterValues(document, paramName) {
      const values = Array.from(document.querySelectorAll(`input[name="${paramName}"][value]`)).map(
        (input) => this.normalizeText(input.value || ""),
      );
      return [...new Set(values)].sort((left, right) => left.localeCompare(right));
    }

    getProductTitles(document) {
      const titles = Array.from(document.querySelectorAll("#product-grid .card-heading a")).map(
        (link) => this.normalizeText(link.getAttribute("title") || link.textContent || ""),
      );
      return [...new Set(titles)];
    }

    parseVehicleTitle(title, makers, carNameSet, carModelSet) {
      const normalizedTitle = this.normalizeText(title.replace(/^\[[^\]]+\]\s*/, ""));
      const maker = makers.find((value) => normalizedTitle.startsWith(`${value} `));

      if (!maker) {
        return null;
      }

      const remainder = this.normalizeText(normalizedTitle.slice(maker.length));
      if (!remainder) {
        return null;
      }

      const yearMatch = remainder.match(/\b(?:19|20)\d{2}\b/);
      const candidateSegment = yearMatch
        ? this.normalizeText(remainder.slice(0, yearMatch.index))
        : remainder;

      const candidateModels = [...carModelSet]
        .filter((value) => {
          const escapedValue = this.escapeRegExp(value);
          return new RegExp(`(?:^|\\s)${escapedValue}(?:\\s|$)`).test(candidateSegment);
        })
        .sort((left, right) => right.length - left.length);

      if (candidateModels.length === 0) {
        return null;
      }

      const carModel = candidateModels[0];
      const modelPattern = new RegExp(`^(.*?)(?:\\s+)${this.escapeRegExp(carModel)}(?:\\s|$)`);
      const carNameMatch = candidateSegment.match(modelPattern);
      const carName = this.normalizeText(carNameMatch?.[1] || "");

      if (!carName || !carNameSet.has(carName)) {
        return null;
      }

      return { maker, carName, carModel };
    }

    buildVehicleIndex(titles, makers, carNames, carModels) {
      const makersSorted = [...makers].sort((left, right) => right.length - left.length);
      const carNameSet = new Set(carNames);
      const carModelSet = new Set(carModels);
      const index = {};

      titles.forEach((title) => {
        const parsed = this.parseVehicleTitle(title, makersSorted, carNameSet, carModelSet);

        if (!parsed) {
          return;
        }

        index[parsed.maker] ||= {};
        index[parsed.maker][parsed.carName] ||= [];

        if (!index[parsed.maker][parsed.carName].includes(parsed.carModel)) {
          index[parsed.maker][parsed.carName].push(parsed.carModel);
        }
      });

      Object.values(index).forEach((carNamesMap) => {
        Object.keys(carNamesMap).forEach((carName) => {
          carNamesMap[carName].sort((left, right) => left.localeCompare(right));
        });
      });

      return index;
    }

    readCache() {
      try {
        const rawValue = window.sessionStorage.getItem(this.cacheKey);
        if (!rawValue) {
          return null;
        }

        const parsed = JSON.parse(rawValue);
        if (!parsed || typeof parsed !== "object" || !parsed.index) {
          return null;
        }

        return parsed;
      } catch (error) {
        return null;
      }
    }

    writeCache(data) {
      try {
        window.sessionStorage.setItem(this.cacheKey, JSON.stringify(data));
      } catch (error) {
        console.error(error);
      }
    }

    async loadVehicleIndex() {
      const cached = this.readCache();
      if (cached) {
        return cached;
      }

      const firstPageHtml = await this.fetchPage(this.buildSearchPageUrl());
      const firstPageDocument = this.parseHtmlDocument(firstPageHtml);
      const makers = this.getFilterValues(firstPageDocument, "filter.p.m.custom.maker");
      const carNames = this.getFilterValues(firstPageDocument, "filter.p.m.custom.car_name");
      const carModels = this.getFilterValues(firstPageDocument, "filter.p.m.custom.car_model");
      const titles = this.getProductTitles(firstPageDocument);
      const pageCount = this.getPageCount(firstPageDocument);

      if (pageCount > 1) {
        const pageHtmlList = await Promise.all(
          Array.from({ length: pageCount - 1 }, (_, index) =>
            this.fetchPage(this.buildSearchPageUrl(index + 2)),
          ),
        );

        pageHtmlList.forEach((html) => {
          titles.push(...this.getProductTitles(this.parseHtmlDocument(html)));
        });
      }

      const uniqueTitles = [...new Set(titles)];
      const index = this.buildVehicleIndex(uniqueTitles, makers, carNames, carModels);
      const payload = { index, makers, carNames, carModels };

      this.writeCache(payload);
      return payload;
    }

    populateMakerOptions() {
      const indexedMakers =
        this.vehicleData?.makers?.filter((maker) => this.vehicleData.index[maker]) || [];
      const makers = indexedMakers.length > 0 ? indexedMakers : this.vehicleData?.makers || [];
      this.populateFieldOptions(this.makerField, makers, { disabled: false });
      this.resetField(this.carNameField, { disabled: true });
      this.resetField(this.carModelField, { disabled: true });
    }

    populateCarNameOptions() {
      const maker = this.makerField.value;
      const indexedCarNames = maker
        ? Object.keys(this.vehicleData?.index?.[maker] || {}).sort((left, right) =>
            left.localeCompare(right),
          )
        : [];
      const carNames = maker
        ? indexedCarNames.length > 0
          ? indexedCarNames
          : this.vehicleData?.carNames || []
        : [];

      this.populateFieldOptions(this.carNameField, carNames, { disabled: carNames.length === 0 });
      this.resetField(this.carModelField, { disabled: true });
    }

    populateCarModelOptions() {
      const maker = this.makerField.value;
      const carName = this.carNameField.value;
      const indexedCarModels = maker && carName ? this.vehicleData?.index?.[maker]?.[carName] || [] : [];
      const carModels =
        maker && carName
          ? indexedCarModels.length > 0
            ? indexedCarModels
            : this.vehicleData?.carModels || []
          : [];

      this.populateFieldOptions(this.carModelField, carModels, { disabled: carModels.length === 0 });
    }

    async initialize() {
      this.fields.forEach((field) => {
        this.setFieldLoading(field);
      });

      try {
        this.vehicleData = await this.loadVehicleIndex();
        this.populateMakerOptions();
      } catch (error) {
        console.error(error);
        this.fields.forEach((field) => this.resetField(field, { disabled: true }));
      } finally {
        this.updateSubmitState();
      }
    }

    onFieldChange(event) {
      if (event.target === this.makerField) {
        this.populateCarNameOptions();
      }

      if (event.target === this.carNameField) {
        this.populateCarModelOptions();
      }

      if (event.target === this.makerField && !this.makerField.value) {
        this.resetField(this.carNameField, { disabled: true });
        this.resetField(this.carModelField, { disabled: true });
      }

      if (event.target === this.carNameField && !this.carNameField.value) {
        this.resetField(this.carModelField, { disabled: true });
      }

      this.updateSubmitState();
    }

    onSubmit(event) {
      this.updateQuery();

      if (this.getSelectedTerms().length === 0) {
        event.preventDefault();
        this.updateSubmitState();
      }
    }
  }

  customElements.define("product-search-form", ProductSearchForm);
}
